import React, { memo, useMemo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Prescription, PrescriptionComplaint, PrescriptionMedicine } from "@/types/prescription";
import { 
  Calendar, 
  Eye, 
  Edit, 
  Copy,
  User,
  Thermometer,
  Heart,
  Activity,
  Gauge,
  Printer
} from "lucide-react";
import { format, isValid, formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { getDoctor } from "@/lib/api";

interface VisitCardProps {
  prescription: Prescription;
  onEdit?: (prescription: Prescription) => void;
  onView?: (prescription: Prescription) => void;
  onCopy?: (prescription: Prescription) => void;
  isLatest?: boolean;
  className?: string;
  doctorName?: string;
  patientName?: string;
  patientAge?: number;
  patientGender?: string;
}

function VisitCard({ 
  prescription, 
  onEdit,
  onView,
  onCopy,
  isLatest = false,
  className,
  doctorName,
  patientName = "Patient Name",
  patientAge = 0,
  patientGender = "Unknown"
}: VisitCardProps) {
  // State for fetched doctor name (if doctor_name is missing from prescription)
  const [fetchedDoctorName, setFetchedDoctorName] = useState<string | null>(null);

  // Fetch doctor name if not available in prescription
  useEffect(() => {
    // Only fetch if doctor_name is missing and doctor_id exists
    if (!prescription.doctor_name && prescription.doctor_id && !doctorName) {
      getDoctor(prescription.doctor_id)
        .then((doctor) => {
          if (doctor?.name) {
            setFetchedDoctorName(doctor.name);
          }
        })
        .catch((error) => {
          console.warn("Failed to fetch doctor name:", error);
          // Keep fetchedDoctorName as null, will use fallback
        });
    }
  }, [prescription.doctor_id, prescription.doctor_name, doctorName]);

  // Get doctor name from prescription data, fetched data, prop, or default
  // Append "Dr." prefix if not present
  const displayDoctorName = useMemo(() => {
    const name = prescription.doctor_name || fetchedDoctorName || doctorName || "Unknown";
    
    // Add "Dr." prefix if not already present
    if (name && !name.trim().toLowerCase().startsWith("dr.") && !name.trim().toLowerCase().startsWith("dr ")) {
      return `Dr. ${name.trim()}`;
    }
    
    return name.trim();
  }, [prescription.doctor_name, fetchedDoctorName, doctorName]);

  // Enhanced date formatting for medical context
  const dateInfo = useMemo(() => {
    if (!prescription?.date) return null;
    
    try {
      const visitDate = new Date(prescription.date);
      if (!isValid(visitDate)) return null;
      
      return {
        formatted: format(visitDate, "dd-MMM-yyyy"),
        time: format(visitDate, "h:mm a"),
        relative: formatDistanceToNow(visitDate, { addSuffix: true })
      };
    } catch (error) {
      console.warn("Date parsing error:", error);
      return null;
    }
  }, [prescription?.date]);

  // Process complaints for medical display
  const complaintsInfo = useMemo(() => {
    if (!Array.isArray(prescription.complaints) || prescription.complaints.length === 0) return null;
    
    const validComplaints = prescription.complaints.filter((c): c is PrescriptionComplaint => 
      Boolean(c?.complaint?.trim())
    );
    
    return {
      list: validComplaints,
      text: validComplaints.map(c => c.complaint.trim().toUpperCase()).join(", ")
    };
  }, [prescription.complaints]);

  // Enhanced medicine processing for medical table
  const medicineInfo = useMemo(() => {
    if (!Array.isArray(prescription.medicines)) return { count: 0, list: [] };
    
    const validMedicines = prescription.medicines.filter((m): m is PrescriptionMedicine => 
      Boolean(m?.product?.trim())
    );
    
    return {
      count: validMedicines.length,
      list: validMedicines
    };
  }, [prescription.medicines]);

  // Investigation info
  const investigationInfo = useMemo(() => {
    if (!Array.isArray(prescription.investigations)) return { count: 0, list: [] };
    const validInvestigations = prescription.investigations
      .filter((i): i is string => typeof i === 'string' && i.trim().length > 0);
    return {
      count: validInvestigations.length,
      list: validInvestigations
    };
  }, [prescription.investigations]);

  // Check if this prescription has substantial content worth copying
  const hasSubstantialContent = useMemo(() => {
    return Boolean(
      prescription.diagnosis?.trim() ||
      medicineInfo.count > 0 ||
      complaintsInfo?.list.length ||
      prescription.advice?.trim() ||
      investigationInfo.count > 0
    );
  }, [prescription.diagnosis, medicineInfo.count, complaintsInfo?.list.length, prescription.advice, investigationInfo.count]);

  // Format medical dosage (e.g., "1-0-1" format)
  const formatDosage = (medicine: PrescriptionMedicine) => {
    if (medicine.dosage) {
      return medicine.dosage;
    }
    return "As directed";
  };

  // Format medicine timing
  const formatTiming = (medicine: PrescriptionMedicine) => {
    if (medicine.taken_when || (medicine as { when?: string }).when) {
      return medicine.taken_when ?? (medicine as { when?: string }).when;
    }
    return "As directed";
  };

  return (
    <Card className={cn(
              "medical-card border border-border shadow-lg overflow-hidden bg-card",
      isLatest && "border-primary shadow-xl",
      className
    )}>
      <CardContent className="p-0">
        {/* Professional Medical Header */}
        <div className={cn(
          "px-4 py-2 border-b-2 border-border bg-primary/10"
        )}>
          <div className="flex items-center justify-between">
            {/* Date and Doctor Info */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="font-semibold text-foreground text-base">
                    {displayDoctorName} • {dateInfo?.formatted || 'Invalid Date'}
                  </div>
                </div>
              </div>
              
              {isLatest && (
                <Badge className="bg-primary text-primary-foreground font-semibold px-2 py-1 text-xs">
                  Latest Visit
                </Badge>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {onCopy && hasSubstantialContent && (
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={() => onCopy(prescription)} 
                  className="medical-button-secondary h-7 px-2 gap-1 text-xs"
                >
                  <Copy className="w-3 h-3" />
                  Copy Rx
                </Button>
              )}
              
              {onEdit && (
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={() => onEdit(prescription)} 
                  className="medical-button-secondary h-7 px-2 gap-1 text-xs"
                >
                  <Edit className="w-3 h-3" />
                  Edit
                </Button>
              )}
               
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onView?.(prescription)}
                className="medical-button-primary h-7 px-2 gap-1 text-xs"
              >
                <Printer className="w-3 h-3" />
                Print
              </Button>
            </div>
          </div>
        </div>

        {/* Medical Content */}
        <div className="p-4 space-y-4">
          {/* Vitals Section */}
          {prescription.vitals && Object.keys(prescription.vitals).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Heart className="w-4 h-4 text-destructive" />
                Vitals:
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                {prescription.vitals.bp && (
                  <div className="flex items-center gap-2">
                    <Gauge className="w-4 h-4 text-primary" />
                    <span className="text-muted-foreground">BP:</span>
                    <span className="font-semibold text-foreground">{prescription.vitals.bp}</span>
                  </div>
                )}
                {prescription.vitals.pulse && (
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-destructive" />
                    <span className="text-muted-foreground">Pulse:</span>
                    <span className="font-semibold text-foreground">{prescription.vitals.pulse}</span>
                  </div>
                )}
                {prescription.vitals.spo2 && (
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-success" />
                    <span className="text-muted-foreground">SPO2:</span>
                    <span className="font-semibold text-foreground">{prescription.vitals.spo2}</span>
                  </div>
                )}
                {prescription.vitals.temp && (
                  <div className="flex items-center gap-2">
                    <Thermometer className="w-4 h-4 text-warning" />
                    <span className="text-muted-foreground">Temperature:</span>
                    <span className="font-semibold text-foreground">{prescription.vitals.temp}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Chief Complaints */}
          {complaintsInfo && (
            <div>
              <h3 className="text-sm font-bold text-foreground mb-2 uppercase tracking-wide">Chief Complaints:</h3>
              <div className="text-sm text-muted-foreground leading-relaxed">
                {complaintsInfo.text}
              </div>
            </div>
          )}

          {/* Prescription Table */}
          {medicineInfo.count > 0 && (
            <div>
              <h3 className="text-sm font-bold text-foreground mb-2 uppercase tracking-wide">Rx:</h3>
              <div className="overflow-x-auto">
                <table className="w-full border border-border text-sm">
                  <thead className="bg-primary/10">
                    <tr>
                      <th className="border border-border px-2 py-1 text-left font-semibold text-xs">#</th>
                      <th className="border border-border px-3 py-1 text-left font-semibold text-xs">Medicine</th>
                      <th className="border border-border px-2 py-1 text-center font-semibold text-xs">Dosage</th>
                      <th className="border border-border px-2 py-1 text-center font-semibold text-xs">When</th>
                      <th className="border border-border px-2 py-1 text-center font-semibold text-xs">Frequency</th>
                      <th className="border border-border px-2 py-1 text-center font-semibold text-xs">Duration</th>
                      <th className="border border-border px-2 py-1 text-center font-semibold text-xs">Qty</th>
                      <th className="border border-border px-2 py-1 text-center font-semibold text-xs">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {medicineInfo.list.map((medicine, index) => (
                      <tr key={medicine.id || `med-${index}`} className="hover:bg-muted/30">
                        <td className="border border-border px-2 py-1 text-center font-semibold text-muted-foreground">
                          {index + 1}
                        </td>
                        <td className="border border-border px-3 py-1">
                          <div className="font-semibold text-foreground text-sm">
                            {medicine.brand_name?.toUpperCase() || medicine.product?.toUpperCase()}
                          </div>
                        </td>
                        <td className="border border-border px-2 py-1 text-center font-mono text-sm">
                          {formatDosage(medicine)}
                        </td>
                        <td className="border border-border px-2 py-1 text-center text-sm">
                          {formatTiming(medicine)}
                        </td>
                        <td className="border border-border px-2 py-1 text-center text-sm">
                          Daily
                        </td>
                        <td className="border border-border px-2 py-1 text-center text-sm">
                          {medicine.duration || '-'}
                        </td>
                        <td className="border border-border px-2 py-1 text-center text-sm">
                          {medicine.quantity || '-'}
                        </td>
                        <td className="border border-border px-2 py-1 text-center text-xs text-muted-foreground">
                          {medicine.notes || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Tests Requested */}
          {investigationInfo.count > 0 && (
            <div>
              <h3 className="text-sm font-bold text-foreground mb-2 uppercase tracking-wide">Tests Requested:</h3>
              <div className="text-sm text-muted-foreground leading-relaxed">
                {investigationInfo.list.map(test => test.toUpperCase()).join(", ")}
              </div>
            </div>
          )}

          {/* Diagnosis */}
          {prescription.diagnosis && (
            <div>
              <h3 className="text-sm font-bold text-foreground mb-2 uppercase tracking-wide">Diagnosis:</h3>
              <div className="text-sm text-muted-foreground leading-relaxed">
                {prescription.diagnosis}
              </div>
            </div>
          )}

          {/* Patient Advice */}
          {prescription.advice && (
            <div>
              <h3 className="text-sm font-bold text-foreground mb-2 uppercase tracking-wide">Patient Advice:</h3>
              <div className="text-sm text-muted-foreground leading-relaxed">
                {prescription.advice}
              </div>
            </div>
          )}

          {/* Next Visit */}
          {prescription.next_visit && (prescription.next_visit.date || prescription.next_visit.notes) && (
            <div>
              <h3 className="text-sm font-bold text-foreground mb-2 uppercase tracking-wide">Next Visit:</h3>
              <div className="text-sm text-muted-foreground">
                {prescription.next_visit.date && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>
                      {prescription.next_visit.date && format(new Date(prescription.next_visit.date), "dd-MMM-yyyy")}
                      {prescription.next_visit.time && ` at ${prescription.next_visit.time}`}
                    </span>
                  </div>
                )}
                {prescription.next_visit.notes && (
                  <div className="text-muted-foreground mt-1 leading-relaxed">
                    {prescription.next_visit.notes}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default memo(VisitCard);